import api from '@anm/api';
import isApiError from '@anm/api/helpers/isApiError';
import { Workspace } from '@anm/api/modules/team';
import { Folder } from '@anm/api/modules/videoFolder';
import { appNotificationActions } from '@anm/store/modules/appNotifications';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

export const useDeleteNestedFoldersAndProjects = (workspace: Workspace, folder?: Folder) => {
  const dispatch = useDispatch();

  const [totalProjects, setTotalProjects] = useState(folder?.projectsCount || 0);
  const [folders, setFolders] = useState<Record<string, string[]>>({});

  const folderLength = Object.keys(folders).reduce((acc, folderId) => acc + folders[folderId].length, 0);

  const [objectDeleted, setObjectDeleted] = useState(0);
  const updateDeletedObjects = () => setObjectDeleted(old => old + 1);

  const updateTotalProjects = (count = 1) => setTotalProjects(old => old + count);

  useEffect(() => {
    if (!folder) return;

    setFolders({ [`${folder.parentId!}`]: [`${folder.id}`] });

    const checkFolders = (folder?: Folder) => {
      folder?.folders.forEach(f => {
        if (!f.parentId) return;

        f.projectsCount && updateTotalProjects(f.projectsCount);
        setFolders(oldFolders => ({
          ...oldFolders,
          [f.parentId!]: [...(oldFolders[`${f.parentId}`] ? [...oldFolders[`${f.parentId}`], `${f.id}`] : [`${f.id}`])]
        }));

        checkFolders(f);
      });
    };

    checkFolders(folder);
  }, [folder?.id]);

  const handleDeleteNestedFoldersAndProjects = async () => {
    if (!folder) return;

    const parentIds = [...(Object.keys(folders) || []).reverse()];

    for (const parentId of parentIds) {
      for (const folderId of folders[parentId] || []) {
        const folderWithProjects = await api().videoFolder.getById({ workspace, folderId: +folderId });

        for (const p of folderWithProjects.projects) {
          try {
            await api().projects.deleteProject({ projectId: p.id, workspace });
            updateDeletedObjects();
          } catch (error) {
            const description =
              (isApiError(error) && error.message) || (error instanceof Error && error.message) || 'Unknown error';
            dispatch(
              appNotificationActions.notify({
                type: 'error',
                title: 'Could not delete project',
                description
              })
            );
          }
        }
        if (folder.parentId !== +parentId) {
          try {
            await api().videoFolder.deleteFolder({ folderId: +folderId, workspace });
            updateDeletedObjects();
          } catch (error) {
            const description =
              (isApiError(error) && error.message) || (error instanceof Error && error.message) || 'Unknown error';
            dispatch(
              appNotificationActions.notify({
                type: 'error',
                title: 'Could not delete folder',
                description
              })
            );
          }
        }
      }
    }
  };

  return [totalProjects, folderLength, objectDeleted, handleDeleteNestedFoldersAndProjects] as const;
};

export default useDeleteNestedFoldersAndProjects;
